import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { getIntegrationProviderDisplayName } from '../../../models/utils/integrationUtils';
import {
	IProviderSetupCounts,
	ISetupIncompleteAccount,
	useAdminIntegrationsHealthQuery,
	useAdminIntegrationsHealthRetryMutation,
} from '../../../queries/Admin/IntegrationsHealth';
import { Input } from '../../../web/components-v2/Input';
import { Label } from '../../../web/components-v2/Label';
import { Pill } from '../../../web/components-v2/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../web/components-v2/Select';
import { Skeleton } from '../../../web/components-v2/Skeleton';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { IFilteredMigrationProvider, MigrationSection } from './MigrationSection';
import { IFilteredSetupIncompleteProvider, SetupIncompleteSection } from './SetupIncompleteSection';
import { SuspendedWithErrorSection } from './SuspendedWithErrorSection';
import { buildRetryReport } from './retryReport';
import { styleSheet } from './styles';
import { ALL_PROVIDERS, matchesProvider, matchesSearch } from './filters';
import { IRetryCandidate, useRetrySelection } from './useRetrySelection';

const PROVIDER_FILTER_ID = 'integrations-health-provider-filter';

function groupByProvider<T extends { provider: Api.IntegrationProvider }>(
	items: T[]
): Map<Api.IntegrationProvider, T[]> {
	const groups = new Map<Api.IntegrationProvider, T[]>();
	for (const item of items) {
		const existing = groups.get(item.provider);
		if (existing) {
			existing.push(item);
		} else {
			groups.set(item.provider, [item]);
		}
	}
	return groups;
}

export const AdminIntegrationsHealthPage: React.FC = () => {
	return (
		<AdminDashboardPageLayout>
			<AdminIntegrationsHealthContent />
		</AdminDashboardPageLayout>
	);
};

const AdminIntegrationsHealthContent: React.FC = () => {
	const { data, isPending, isError } = useAdminIntegrationsHealthQuery();
	const [search, setSearch] = React.useState('');
	const [providerFilter, setProviderFilter] = React.useState<Api.IntegrationProvider | typeof ALL_PROVIDERS>(
		ALL_PROVIDERS
	);

	const normalizedSearch = search.trim().toLowerCase();
	const isFiltered = !!normalizedSearch || providerFilter !== ALL_PROVIDERS;

	// A field the API has not shipped yet reads as unavailable, not as nothing being in that state.
	const setupIncomplete: ISetupIncompleteAccount[] | null = data?.setupIncomplete ?? null;
	const setupByProvider: IProviderSetupCounts[] | null = data?.setupByProvider ?? null;

	const providerOptions = React.useMemo(() => {
		const providers = new Set<Api.IntegrationProvider>();
		(data?.suspendedWithError ?? []).forEach(s => providers.add(s.provider));
		setupIncomplete?.forEach(a => providers.add(a.provider));
		setupByProvider?.forEach(p => providers.add(p.provider));
		data?.pendingMigration?.forEach(p => providers.add(p.provider));
		return Array.from(providers).sort((a, b) =>
			getIntegrationProviderDisplayName(a).localeCompare(getIntegrationProviderDisplayName(b))
		);
	}, [data, setupIncomplete, setupByProvider]);

	const filteredSuspendedWithError = React.useMemo(
		() =>
			(data?.suspendedWithError ?? []).filter(
				s => matchesProvider(providerFilter, s.provider) && matchesSearch(normalizedSearch, s.accountId, s.companyName)
			),
		[data, providerFilter, normalizedSearch]
	);

	const filteredSetupIncomplete: IFilteredSetupIncompleteProvider[] = React.useMemo(() => {
		if (!setupIncomplete) {
			return [];
		}
		const accountsByProvider = groupByProvider(
			setupIncomplete.filter(
				a => matchesProvider(providerFilter, a.provider) && matchesSearch(normalizedSearch, a.accountId, a.companyName)
			)
		);
		const countsByProvider = new Map((setupByProvider ?? []).map(p => [p.provider, p]));
		const providers = new Set([...accountsByProvider.keys(), ...countsByProvider.keys()]);

		return Array.from(providers)
			.filter(provider => matchesProvider(providerFilter, provider))
			.map(provider => {
				const accounts = accountsByProvider.get(provider) ?? [];
				const counts = countsByProvider.get(provider);
				const setupIncompleteCount = counts?.setupIncompleteCount ?? accounts.length;
				return {
					provider,
					enabledCount: counts?.enabledCount ?? 0,
					setupIncompleteCount,
					accounts,
					visibleSetupIncompleteCount: normalizedSearch ? accounts.length : setupIncompleteCount,
				};
			})
			.filter(p => p.visibleSetupIncompleteCount > 0);
	}, [setupIncomplete, setupByProvider, providerFilter, normalizedSearch]);

	const filteredMigration: IFilteredMigrationProvider[] = React.useMemo(
		() =>
			(data?.pendingMigration ?? [])
				.filter(p => matchesProvider(providerFilter, p.provider))
				.map(p => {
					const pendingAccounts = p.pendingAccounts.filter(a =>
						matchesSearch(normalizedSearch, a.accountId, a.companyName)
					);
					return {
						...p,
						pendingAccounts,
						visiblePendingCount: normalizedSearch ? pendingAccounts.length : p.pendingCount,
					};
				})
				.filter(p => p.visiblePendingCount > 0),
		[data, providerFilter, normalizedSearch]
	);

	const suspendedWithErrorGroups = React.useMemo(
		() => groupByProvider(filteredSuspendedWithError),
		[filteredSuspendedWithError]
	);

	const retryCandidates: IRetryCandidate[] = React.useMemo(
		() =>
			filteredSuspendedWithError.map(row => ({
				accountId: row.accountId,
				provider: row.provider,
				retryable: !row.needsConfiguration,
			})),
		[filteredSuspendedWithError]
	);
	const selection = useRetrySelection(retryCandidates);

	// Mirrors the endpoint, which requires both. ModifyAccount alone is held by reseller admins, so checking it
	// on its own would offer the control to someone the server refuses.
	const userSession = useAdminUserSession();
	const canRetry =
		!!userSession.permissions?.[AdminPermission.ModifyAccount] &&
		!!userSession.permissions?.[AdminPermission.ViewErrorDetail];

	const retryMutation = useAdminIntegrationsHealthRetryMutation({
		onSuccess: results => {
			selection.clear();
			const { severity, text } = buildRetryReport(results);
			toast[severity](text);
		},
	});

	const countsKnown = !isPending && !isError;
	const totalSuspendedWithError = (data?.suspendedWithError ?? []).length;
	const totalSetupIncomplete = setupByProvider
		? setupByProvider.reduce((sum, p) => sum + p.setupIncompleteCount, 0)
		: (setupIncomplete?.length ?? 0);
	const totalPendingMigration = (data?.pendingMigration ?? []).reduce((sum, p) => sum + p.pendingCount, 0);
	const totalMigrated = (data?.pendingMigration ?? []).reduce((sum, p) => sum + p.migratedCount, 0);
	const migrationUnavailable = countsKnown && data != null && data.pendingMigration == null;

	return (
		<div className={css(styleSheet.page)}>
			<header className={css(styleSheet.pageHeader)}>
				<div>
					<h1 className={css(styleSheet.pageTitle)}>Integrations Health</h1>
					<p className={css(styleSheet.pageSubtitle)}>Triage integrations that failed or never finished setup.</p>
				</div>
				<div className={css(styleSheet.controlsRow)}>
					<Input
						type='search'
						aria-label='Search by account name or id'
						placeholder='Search by account name or id'
						styles={[styleSheet.searchInput]}
						value={search}
						onChange={e => setSearch(e.target.value)}
					/>
					<div className={css(styleSheet.filterGroup)}>
						<Label htmlFor={PROVIDER_FILTER_ID}>Integration</Label>
						<Select
							value={providerFilter}
							onValueChange={value => setProviderFilter(value as Api.IntegrationProvider | typeof ALL_PROVIDERS)}
						>
							<SelectTrigger id={PROVIDER_FILTER_ID}>
								<span>
									{providerFilter === ALL_PROVIDERS
										? 'All integrations'
										: getIntegrationProviderDisplayName(providerFilter)}
								</span>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL_PROVIDERS}>All integrations</SelectItem>
								{providerOptions.map(provider => (
									<SelectItem key={provider} value={provider}>
										{getIntegrationProviderDisplayName(provider)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
			</header>

			<div className={css(styleSheet.statRow)}>
				<StatCard
					label='Suspended with error'
					value={countsKnown ? totalSuspendedWithError : '—'}
					valueStyle={styleSheet.statSuspended}
				/>
				<StatCard
					label='Awaiting configuration'
					value={countsKnown && setupIncomplete ? totalSetupIncomplete : '—'}
					valueStyle={styleSheet.statSetupIncomplete}
					flagged
				/>
				<StatCard
					label='Pending V1→V2 migration'
					value={countsKnown && !migrationUnavailable ? totalPendingMigration : '—'}
					valueStyle={styleSheet.statPending}
				/>
				<StatCard
					label='Migrated to V2'
					value={countsKnown && !migrationUnavailable ? totalMigrated : '—'}
					valueStyle={styleSheet.statMigrated}
				/>
			</div>

			<HealthSection
				title='Suspended with Error'
				chip={
					countsKnown ? (
						<Pill variant='tag' color='red' label={`${filteredSuspendedWithError.length}`} size='small' />
					) : null
				}
				isPending={isPending}
				isError={isError}
				errorText="Couldn't load suspended integrations."
				isEmpty={suspendedWithErrorGroups.size === 0}
				emptyText={
					isFiltered
						? 'No suspended integrations match the current filter.'
						: 'No integrations are currently suspended with an error.'
				}
			>
				<SuspendedWithErrorSection
					groups={suspendedWithErrorGroups}
					expandGroups={!!normalizedSearch}
					selection={selection}
					candidates={retryCandidates}
					canRetry={canRetry}
					isRetrying={retryMutation.isPending}
					onRetry={() => retryMutation.mutate(selection.selectedTargets)}
				/>
			</HealthSection>

			<HealthSection
				title='Awaiting Configuration'
				chip={
					countsKnown && setupIncomplete ? (
						<Pill
							variant='tag'
							color='orange'
							label={`${filteredSetupIncomplete.reduce((sum, p) => sum + p.visibleSetupIncompleteCount, 0)}`}
							size='small'
						/>
					) : null
				}
				meta={countsKnown && setupIncomplete ? 'Enabled, but will never run until setup is finished' : undefined}
				isPending={isPending}
				isError={isError}
				errorText="Couldn't load configuration status."
				isUnavailable={countsKnown && !setupIncomplete}
				unavailableText='Configuration status is not available from this API version yet.'
				isEmpty={filteredSetupIncomplete.length === 0}
				emptyText={
					isFiltered
						? 'No accounts awaiting configuration match the current filter.'
						: 'Every enabled integration is fully configured.'
				}
			>
				<SetupIncompleteSection
					providers={filteredSetupIncomplete}
					expandGroups={!!normalizedSearch}
					isSearching={!!normalizedSearch}
				/>
			</HealthSection>

			<HealthSection
				title='Needs V1 → V2 Migration'
				chip={
					countsKnown && !migrationUnavailable ? (
						<Pill
							variant='tag'
							color='orange'
							label={`${filteredMigration.reduce((sum, p) => sum + p.visiblePendingCount, 0)}`}
							size='small'
						/>
					) : null
				}
				meta={
					countsKnown && !migrationUnavailable
						? `Overall: ${totalMigrated} / ${totalMigrated + totalPendingMigration} accounts migrated to V2`
						: undefined
				}
				isPending={isPending}
				isError={isError}
				errorText="Couldn't load migration status."
				isUnavailable={migrationUnavailable}
				unavailableText="Migration reporting isn't available in this environment — it comes from a report view that only exists in production."
				isEmpty={filteredMigration.length === 0}
				emptyText={
					isFiltered
						? 'No accounts pending migration match the current filter.'
						: 'Every account with a V1 integration has migrated to V2.'
				}
			>
				<MigrationSection providers={filteredMigration} expandGroups={!!normalizedSearch} />
			</HealthSection>
		</div>
	);
};

const StatCard: React.FC<{
	label: string;
	value: React.ReactNode;
	valueStyle?: Parameters<typeof css>[0];
	flagged?: boolean;
}> = ({ label, value, valueStyle, flagged }) => (
	<div className={css(styleSheet.statCard, flagged && styleSheet.statCardFlagged)}>
		<span className={css(styleSheet.statLabel)}>{label}</span>
		<span className={css(styleSheet.statValue, valueStyle)}>{value}</span>
	</div>
);

interface IHealthSectionProps {
	title: string;
	chip?: React.ReactNode;
	meta?: string;
	isPending: boolean;
	isError: boolean;
	errorText: string;
	isUnavailable?: boolean;
	unavailableText?: string;
	isEmpty: boolean;
	emptyText: string;
	children: React.ReactNode;
}

const HealthSection: React.FC<IHealthSectionProps> = ({
	title,
	chip,
	meta,
	isPending,
	isError,
	errorText,
	isUnavailable,
	unavailableText,
	isEmpty,
	emptyText,
	children,
}) => (
	<section className={css(styleSheet.section)} aria-label={title}>
		<div className={css(styleSheet.sectionHeader)}>
			<div className={css(styleSheet.sectionHeaderLeft)}>
				<h2 className={css(styleSheet.sectionTitle)}>{title}</h2>
				{chip}
			</div>
			{meta ? <span className={css(styleSheet.sectionMeta)}>{meta}</span> : null}
		</div>
		{isPending ? (
			<SectionSkeleton />
		) : isError ? (
			<div className={css(styleSheet.emptyState)}>{errorText}</div>
		) : isUnavailable ? (
			<div className={css(styleSheet.emptyState)}>{unavailableText}</div>
		) : isEmpty ? (
			<div className={css(styleSheet.emptyState)}>{emptyText}</div>
		) : (
			children
		)}
	</section>
);

const SectionSkeleton: React.FC = () => (
	<div className={css(styleSheet.emptyState)}>
		<Skeleton styles={[styleSheet.skeletonLine]} />
		<Skeleton styles={[styleSheet.skeletonLine, styleSheet.skeletonLineShort]} />
	</div>
);
